import React, {createRef, ReactNode, RefObject} from "react";
import FileHelper from "../../../shared/helper/FileHelper";
import NumberHelper from "../../../shared/helper/NumberHelper";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import PortalDocument, {PortalDocumentInterface} from "../PortalDocument";
import MimeTypes from "../mimetype/MimeTypes";
import {PortalGlobals} from "../../Portal";
import MessageBox from "../../../shared/components/MessageBox";
import Exception from "../../../shared/exception/Exception";
import {PortalDocumentManagerGlobals} from "../PortalDocumentManager";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import i18next from "i18next";
import ErrorCode from "../../../shared/exception/ErrorCode";
import {
	Attachment,
	Description,
	DocMdp,
	DocumentFile,
	MetadataFormsFormat,
	MetadataInformation,
	MetadataPdfa,
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {MessageType} from "../../../shared/log/MessageInterface";
import Immutable from "../../../shared/immutable/Immutable";
import Translate from "../../../shared/translation/Translate";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export interface DocumentInfoProperties extends PortalDocumentManagerGlobals, PortalGlobals, I18nGlobals, DebugGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
}

interface DocumentInfoStatus {
	numberOfPages: number;
}

export default class DocumentInfo extends PortalPureComponent<DocumentInfoProperties, DocumentInfoStatus> {
	private viewerElement: RefObject<HTMLDivElement>;
	public static readonly hiddenMessages: Array<ErrorCode> = [
		ErrorCode.PDF_PASSWORD_MISSING,
		ErrorCode.PDF_PASSWORD_OWNER,
		ErrorCode.PDF_PASSWORD_USER,
		ErrorCode.PDF_CERTIFICATE_KEYPAIR_FAILURE,
		ErrorCode.PDF_CERTIFICATE_PRIVATE_KEY_FAILURE,
		ErrorCode.PDF_CERTIFICATE_CERTIFICATE_FAILURE,
		ErrorCode.PDF_CERTIFICATE_KEYPAIR_MISSING
	];

	constructor(props: Readonly<DocumentInfoProperties>) {
		super(props);

		this.state = {
			numberOfPages: 0
		};

		this.viewerElement = createRef<HTMLDivElement>();
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.setNumberOfPages();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<DocumentInfoProperties>, prevState: Readonly<DocumentInfoStatus>
	): Promise<void> {
		await this.setNumberOfPages();
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument)) {
			return;
		}

		let selected: PortalDocumentInterface | undefined = this.props.portalDocument!.get();
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(selected?.id!);

		let numberOfPages: number = await portalDocument.getNumberOfPages();

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument)) {
			return null;
		}

		let exactSize: string = "0";

		let selected: PortalDocumentInterface = this.props.portalDocument!.get();
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(selected?.id!);
		let selectedInfo: DocumentFile = portalDocument.getDocumentFile()!;

		exactSize = NumberHelper.formatString(FunctionHelper.applyValue(
			selectedInfo.fileSize, DocumentFile.getFileSizeDefault()
		), 0);

		let values: Array<{ label: any, value: any }> = [
			{
				label: <Translate {...this.props} data="info.filename" namespace="sidebar"/>,
				value: <Translate {...this.props} data="info.values.filename" namespace="sidebar"
								  values={{
									  name: selectedInfo.fileName,
									  extension: selectedInfo.fileExtension
								  }}/>
			},
			{
				label: <Translate {...this.props} data="info.filesize" namespace="sidebar"/>,
				value: <Translate {...this.props} data="info.values.filesize" namespace="sidebar"
								  values={{
									  size: FileHelper.formatFileSize(FunctionHelper.applyValue(
										  selectedInfo.fileSize, DocumentFile.getFileSizeDefault()
									  ), 2),
									  exactsize: exactSize
								  }}/>
			},
			{
				label: <Translate {...this.props} data="info.filetype" namespace="sidebar"/>,
				value: <Translate {...this.props} data="info.values.filetype" namespace="sidebar"
								  values={{
									  mimeType: selectedInfo.mimeType,
									  mimeTypeId: selectedInfo.fileTypeId
								  }}/>
			},
			{
				label: <Translate {...this.props} data="info.pages" namespace="sidebar"/>,
				value: this.state.numberOfPages
			}
		];

		if (FunctionHelper.applyValue(
			portalDocument.metadata?.information?.pdfa?.part, MetadataPdfa.getPartDefault()
		) !== "") {
			values.push({
				label: <Translate {...this.props} data="info.pdfa" namespace="sidebar"/>,
				value: <Translate {...this.props} data="info.values.pdfa" namespace="sidebar"
								  values={{
									  part: portalDocument.metadata?.information?.pdfa?.part,
									  conformance: portalDocument.metadata?.information?.pdfa?.conformance
								  }}/>
			});
		}

		if (portalDocument.hasSignature()) {
			values.push({
				label: <Translate {...this.props} data="info.signature" namespace="sidebar"/>,
				value: <Translate {...this.props} data="info.values.yes" namespace="sidebar"/>
			});
		}

		let certificationLevel: DocMdp = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
		);
		if (certificationLevel !== DocMdp.None) {
			values.push({
				label: <Translate {...this.props} data="info.certification_level" namespace="sidebar"/>,
				value: certificationLevel
			});
		}

		let formtype: MetadataFormsFormat = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
		);
		if (formtype !== MetadataFormsFormat.None) {
			let formLabel: ReactNode = <Translate {...this.props} data="info.values.acroform" namespace="sidebar"/>;
			switch (formtype) {
				case MetadataFormsFormat.DynamicXFA:
					formLabel = <Translate {...this.props} data="info.values.dynXfa" namespace="sidebar"/>;
					break;
				case MetadataFormsFormat.StaticXFA:
					formLabel = <Translate {...this.props} data="info.values.staticXfa" namespace="sidebar"/>;
					break;
			}

			values.push({
				label: <Translate {...this.props} data="info.form" namespace="sidebar"/>, value: formLabel
			});
		}

		let attachments: Array<Attachment> = portalDocument.getAttachments();
		if (attachments.length > 0) {
			values.push({
				label: <Translate {...this.props} data="info.attachments" namespace="sidebar"/>,
				value: attachments.length
			});
		}

		if (portalDocument.getMimeType() === MimeTypes.PDF) {
			let pdfTitle: string = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.title, Description.getTitleDefault()
			);
			if (!FunctionHelper.isEmpty(pdfTitle)) {
				values.push({
					label: <Translate {...this.props} data="info.pdf.title" namespace="sidebar"/>, value: pdfTitle
				});
			}

			let authors: string = '';
			let first: boolean = true;
			for (let author of FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.author, Description.getAuthorDefault()
			)) {
				authors += (!first ? "; " : "") + author.name;
				first = false;
			}

			if (authors !== "") {
				values.push({
					label: <Translate {...this.props} data="info.pdf.author" namespace="sidebar"/>, value: authors
				});
			}

			let pdfSubject: string = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.subject, Description.getSubjectDefault()
			);
			if (pdfSubject !== "") {
				values.push({
					label: <Translate {...this.props} data="info.pdf.subject" namespace="sidebar"/>, value: pdfSubject
				});
			}

			let keywords: string = '';
			first = true;
			for (let keyword of FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.keywords, Description.getKeywordsDefault()
			)) {
				keywords += (!first ? "; " : "") + keyword.keyword;
				first = false;
			}

			if (keywords !== "") {
				values.push({
					label: <Translate {...this.props} data="info.pdf.keywords" namespace="sidebar"/>, value: keywords
				});
			}

			let pdfApplication: string = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.application, Description.getApplicationDefault()
			);
			if (pdfApplication !== "") {
				values.push({
					label: <Translate {...this.props} data="info.pdf.application" namespace="sidebar"/>,
					value: pdfApplication
				});
			}

			let pdfProducer: string = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.producer, Description.getProducerDefault()
			);
			if (pdfProducer !== "") {
				values.push({
					label: <Translate {...this.props} data="info.pdf.producer" namespace="sidebar"/>, value: pdfProducer
				});
			}
		}

		let customValues: Array<{ label: any, value: any }> = [];
		for (let customValue of FunctionHelper.applyValue(
			portalDocument.metadata?.information?.description?.custom, Description.getCustomDefault()
		)) {
			customValues.push({
				label: customValue.key, value: customValue.value
			})
		}

		let error: undefined | Exception;
		if (FunctionHelper.exists(portalDocument.getDocumentFile()?.error)) {
			error = Exception.parseExceptionBean(portalDocument.getDocumentFile()!.error!);
		}

		return (
			<>
				{FunctionHelper.exists(error) && error?.code !== 0 &&
					DocumentInfo.hiddenMessages.indexOf(FunctionHelper.applyValue(error?.code, 0)) === -1 &&
					<MessageBox type={MessageType.DANGER} language={i18next.language} stacktrace={
						<>
							<p>{error!.message} ({error!.code})</p>
							<pre>
								{error!.stack}
							</pre>
						</>
					}>
						{error!.message}
					</MessageBox>
				}
				<DataTable value={values} responsiveLayout={"scroll"}>
					<Column field="label"/>
					<Column field="value"/>
				</DataTable>
				{customValues.length > 0 &&
					<DataTable value={customValues} responsiveLayout={"scroll"}>
						<Column field="label"/>
						<Column field="value"/>
					</DataTable>
				}
			</>
		);
	}
}